import { For, Show } from "solid-js";
import { MAP_CHANGE_STATUS } from "../../enums/map";
import { mapConfidenceLabel, mapStatusLabel } from "./mapLabels";
import type { MapPreviewAction } from "./mapPreviewActions";
import {
  MAP_ACTION_CATEGORIES,
  mapActionCategoryEmptyText,
  mapActionCategoryLabel,
  mapActionEvidenceLabel,
  mapFutureBriefLabel,
} from "./mapPreviewConfig";

export function MapChangeQueue(props: {
  actions: MapPreviewAction[];
  selectedId: string | null;
  emptyReason: string | null;
  onSelect: (id: string) => void;
}) {
  const actionsFor = (category: MapPreviewAction["category"]) =>
    props.actions.filter((action) => action.category === category);
  const needsContext = () =>
    props.actions.filter((action) => action.category === null);
  return (
    <section
      class="map-change-queue map-architecture-board"
      aria-labelledby="map-change-queue-title"
    >
      <header class="map-section-head">
        <div>
          <span class="signal-mini-kicker">Draft architecture</span>
          <h2 id="map-change-queue-title">Suggested structural directions</h2>
          <p>
            Deterministic suggestions grounded in current structure and
            findings. They are not approved architecture decisions.
          </p>
        </div>
        <span>{props.actions.length} suggested actions</span>
      </header>
      <Show
        when={props.actions.length > 0}
        fallback={
          <div class="map-plan-empty">
            <strong>No supported draft actions yet</strong>
            <p>
              {props.emptyReason ??
                "The current scan does not provide enough evidence for an architecture direction."}
            </p>
          </div>
        }
      >
        <div class="map-action-groups">
          <For each={MAP_ACTION_CATEGORIES}>
            {(category) => (
              <section class={`map-action-group is-${category}`}>
                <header>
                  <span>{mapActionCategoryLabel(category)}</span>
                  <strong>{actionsFor(category).length}</strong>
                </header>
                <Show
                  when={actionsFor(category).length > 0}
                  fallback={
                    <p class="map-action-empty">
                      {mapActionCategoryEmptyText(category)}
                    </p>
                  }
                >
                  <div class="map-action-list">
                    <For each={actionsFor(category)}>
                      {(action) => (
                        <ActionCard
                          action={action}
                          selected={props.selectedId === action.change.id}
                          onSelect={props.onSelect}
                        />
                      )}
                    </For>
                  </div>
                </Show>
              </section>
            )}
          </For>
        </div>
        <Show when={needsContext().length > 0}>
          <section class="map-needs-context">
            <header>
              <div>
                <span class="signal-mini-kicker">Outside draft categories</span>
                <h3>Needs context</h3>
              </div>
              <strong>{needsContext().length}</strong>
            </header>
            <p>
              These directions need client context before they can be classified
              as an architecture action.
            </p>
            <div class="map-action-list">
              <For each={needsContext()}>
                {(action) => (
                  <ActionCard
                    action={action}
                    selected={props.selectedId === action.change.id}
                    onSelect={props.onSelect}
                  />
                )}
              </For>
            </div>
          </section>
        </Show>
      </Show>
    </section>
  );
}

function ActionCard(props: {
  action: MapPreviewAction;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const change = () => props.action.change;
  const context = () =>
    props.action.relatedFlowLabels[0] ?? change().currentGroupLabel;
  const evidenceLabel = () =>
    mapActionEvidenceLabel(
      props.action.evidenceState,
      props.action.evidenceSeverity,
    );
  return (
    <button
      type="button"
      class="map-change-record map-action-card"
      classList={{
        "is-selected": props.selected,
        "is-parked": change().status === MAP_CHANGE_STATUS.DISMISSED,
      }}
      aria-pressed={props.selected}
      aria-label={`${change().title}. ${mapStatusLabel(
        change().status,
        change().adjusted,
      )}. ${evidenceLabel()}. ${mapConfidenceLabel(change().confidence)}.`}
      onClick={() => props.onSelect(change().id)}
    >
      <span class="map-action-card-head">
        <i
          class={`is-${
            props.action.evidenceSeverity ?? props.action.evidenceState
          }`}
        >
          {evidenceLabel()}
        </i>
        <small>{mapStatusLabel(change().status, change().adjusted)}</small>
      </span>
      <strong>{change().title}</strong>
      <p>{change().summary}</p>
      <span class="map-action-context">{context()}</span>
      <span class="map-change-meta">
        <span>
          {change().pages.length} affected{" "}
          {change().pages.length === 1 ? "page" : "pages"}
        </span>
        <Show when={props.action.concernCount > 0}>
          <span>
            {props.action.concernCount} causal{" "}
            {props.action.concernCount === 1 ? "concern" : "concerns"}
          </span>
        </Show>
      </span>
      <span class="map-action-brief">
        {mapFutureBriefLabel(props.action.futureBriefLevel)}
      </span>
    </button>
  );
}
